import { useState } from "react";
import { NavLink, useNavigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  LayoutDashboard, Users2, TrendingUp, Flame, UserRound, LogOut, Menu, ChevronRight,
} from "lucide-react";

function navFor(user) {
  if (user?.role === "admin" || user?.role === "reviewer") {
    const items = [
      { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
      { to: "/roster", label: "Roster", icon: Users2 },
      { to: "/stock-watch", label: "Stock Watch", icon: TrendingUp },
      { to: "/alerts", label: "Heat Alerts", icon: Flame },
    ];
    if (user?.role === "admin") items.push({ to: "/team", label: "Team & Access", icon: UserRound });
    return items;
  }
  const own = user?.athlete_id ? `/athlete/${user.athlete_id}` : "/";
  return [{ to: own, label: "My Profile", icon: UserRound, end: true }];
}

function NavList({ items, onNavigate }) {
  return (
    <nav className="flex flex-col gap-1" data-testid="main-nav">
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          onClick={onNavigate}
          data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
          className={({ isActive }) =>
            cn(
              "group flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors",
              isActive
                ? "bg-secondary text-white"
                : "text-muted-foreground hover:text-white hover:bg-secondary/60"
            )
          }
        >
          <item.icon className="h-4 w-4" />
          <span className="flex-1">{item.label}</span>
          <ChevronRight className="h-3.5 w-3.5 opacity-0 group-hover:opacity-60 transition-opacity" />
        </NavLink>
      ))}
    </nav>
  );
}

function SidebarInner({ user, onLogout, onNavigate }) {
  return (
    <div className="flex h-full flex-col">
      <div className="px-5 py-6 border-b border-border">
        <Logo size={38} />
      </div>
      <div className="flex-1 overflow-y-auto px-3 py-4">
        <Overline className="px-3 mb-2">{user?.role === "admin" ? "Back office" : "Portal"}</Overline>
        <NavList items={navFor(user)} onNavigate={onNavigate} />
      </div>
      <div className="border-t border-border p-3">
        <div className="px-2 pb-3">
          <div className="text-sm font-medium text-white truncate" data-testid="current-user-email">{user?.email}</div>
          <div className="overline mt-0.5">{user?.role}</div>
        </div>
        <Button
          variant="outline"
          className="w-full justify-start gap-2 border-border text-muted-foreground hover:text-white"
          onClick={onLogout}
          data-testid="logout-button"
        >
          <LogOut className="h-4 w-4" /> Sign out
        </Button>
      </div>
    </div>
  );
}

function Overline({ children, className }) {
  return <div className={cn("overline", className)}>{children}</div>;
}

export function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex fixed inset-y-0 left-0 w-64 flex-col bg-[#080808] border-r border-border z-30">
        <SidebarInner user={user} onLogout={handleLogout} />
      </aside>

      {/* Mobile top bar */}
      <header className="md:hidden sticky top-0 z-30 flex items-center justify-between bg-[#080808]/95 backdrop-blur border-b border-border px-4 py-3">
        <Logo size={32} showWordmark={false} />
        <span className="font-display text-lg text-white">The Merch Dash</span>
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" data-testid="mobile-menu-button">
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-72 bg-[#080808] border-border p-0">
            <SidebarInner user={user} onLogout={handleLogout} onNavigate={() => setOpen(false)} />
          </SheetContent>
        </Sheet>
      </header>

      <main className="md:ml-64 min-h-screen" key={location.pathname}>
        {user?.role === "reviewer" && (
          <div className="bg-[#E1FF00] text-black text-center text-xs font-semibold py-2 px-4" data-testid="reviewer-banner">
            READ-ONLY PREVIEW REVIEW · Temporary access · Nothing here is published or editable
          </div>
        )}
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10 py-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
